import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import {
  useStoreDelegateLink,
  useVerifyStoreDelegate,
} from '../../../../shared/hooks/linkHook';
import { SlotRef } from '../../common/components/Slot/types';
import { PreviewLayout } from '../Layout/PreviewLayout';
import {
  LinkPageBackground,
  LinkPageContainer,
} from '../Preview/MessageComponents';
import { PreviewVerification } from '../Preview/Verification';

import { SuccessMessage } from './StoreDelegateAgreement';

export const StoreDelegateVerification = () => {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.storeDelegate.verification',
  });

  const birthInput = useRef<SlotRef>(null);

  const {
    mutate: verifyStoreDelegate,
    isSuccess,
    error: errorVerifyStoreDelegate,
  } = useVerifyStoreDelegate({
    onError() {
      birthInput.current?.inputs[0]?.focus();
      queryClient.invalidateQueries({ queryKey: ['StoreDelegateLink', id] });
    },
  });
  const { data: link, isError } = useStoreDelegateLink(id);
  const failure = link?.failureCount ?? 0;
  const isAlreadyHandled = errorVerifyStoreDelegate?.response?.status === 404;

  useEffect(() => {
    if (isError) {
      navigate('/');
    }
  }, [isError, navigate]);

  return isSuccess || isAlreadyHandled ? (
    <PreviewLayout>
      <LinkPageBackground>
        <LinkPageContainer>
          <SuccessMessage />
        </LinkPageContainer>
      </LinkPageBackground>
    </PreviewLayout>
  ) : (
    <PreviewVerification
      failure={failure}
      onAnswer={function (birth: string, gender: string): void {
        verifyStoreDelegate({
          uri: id,
          birth,
          gender,
        });
      }}
      failedTitle={`${t('title')}`}
      failedMessage={
        <Trans
          t={t}
          i18nKey="content"
          components={[<Link to="/send-images" />]}
        />
      }
    />
  );
};
